import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { listPermissions, createRoleWithPermissions } from "../../services/systemRoleService";
import { PermissionSelector } from "../../components/admin/PermissionSelector";
import {
  TOKENS,
  Icon,
  ICONS,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
} from "../../components/common/DashboardUI";

// Pull a human message out of an axios error.
function errMessage(err, fallback) {
  const data = err?.response?.data;
  if (data?.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }
  return data?.message || fallback;
}

// Dedicated full-page role creation flow (Super Admin only — see the
// `requireSuperAdmin` route guard in App.js). Sits alongside the Roles &
// Permissions page's own "Add Role" modal; both call the same
// createRoleWithPermissions() service so a role created here behaves
// identically (shows up in the roles list and in the user role-assignment
// dropdown, since both pull the live roles list from the backend).
export default function AddRole() {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listPermissions()
      .then(setPermissions)
      .catch((err) => toast.error(errMessage(err, "Could not load permissions.")))
      .finally(() => setLoadingPermissions(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setSaving(true);
    try {
      await createRoleWithPermissions({
        name: name.trim(),
        description: description.trim() || null,
        permission_ids: [...selected],
      });
      toast.success("Role created.");
      navigate("/admin/roles");
    } catch (err) {
      setError(errMessage(err, "Could not create the role."));
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-[1400px] min-w-0">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
            Create New Role
          </h1>
          <p className="text-sm text-gray-500">
            Define a new role and choose the permissions its users will inherit.
          </p>
        </div>
        <button onClick={() => navigate("/admin/roles")} className={btnSecondary}>
          Back to Roles
        </button>
      </div>

      {/* Single-column stack: each card spans the full available width. The
          permission grid sizes its own columns off that width (auto-fill/
          minmax), so it always has the full card to work with instead of
          sharing space with a fixed-width sibling column. */}
      <form onSubmit={handleSubmit} className="space-y-6 min-w-0">
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
          <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-4">Role Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className={labelClass}>Role name</label>
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className={labelClass}>
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                className={`${inputClass} resize-none`}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 min-w-0">
          <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-4">Permissions</h2>
          {loadingPermissions ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading permissions…</p>
            </div>
          ) : (
            <PermissionSelector permissions={permissions} selected={selected} onChange={setSelected} />
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Actions */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 flex flex-col sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/roles")}
            className={`${btnSecondary} w-full sm:w-auto sm:px-8`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || loadingPermissions || !name.trim()}
            className={`${btnPrimary} w-full sm:w-auto sm:px-8`}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            {saving ? "Creating…" : "Create Role"}
          </button>
        </div>
      </form>
    </div>
  );
}
